import Color from 'color'

import { gray100 } from 'src/styles/color-theme'
import { rawTheme } from 'src/styles/raw'
import { resolveColorString } from 'src/utils/color'

import { TagColorType } from './types'

// Real hex, not `var(--wh-*)`: PRESET_COLORS feeds `Color().darken/lighten`,
// which parses the string and throws on `var()` (PRODUCT-154). Resolves light —
// tag palettes go theme-aware in Phase 4. `gray100` (the on-chip text color)
// stays a var since it's only used in CSS context.
const c = rawTheme('light')

export const PRESET_COLORS: Record<TagColorType, string> = {
  default: c.gray700,
  gray: c.gray700,
  red: c.red600,
  orange: c.orange600,
  volcano: c.volcano600,
  yellow: c.yellow600,
  gold: c.gold600,
  green: c.green500,
  lime: c.lime600,
  cyan: c.cyan500,
  blue: c.blue600,
  geekblue: c.geekBlue600,
  purple: c.purple600,
  pink: c.magenta600,
}

/**
 * Resolve whatever a caller passed as a tag color into something the `color`
 * package can actually parse.
 *
 * A tag's color is not always one of our `TagColorType` presets: a waste
 * fraction's `color` is free-form persisted data written by a color picker (or
 * an import, or an older build), and every `generate*Styles` helper below runs
 * `Color(...)` on it INSIDE a styled-components interpolation — i.e. during
 * render. An unparseable value therefore threw
 * `Unable to parse color from string: …` out of render and the app's error
 * boundary replaced the whole page with "Oops! Something went wrong".
 *
 * The value that actually hit users was `var(--wh-gray-800)` (PRODUCT-154
 * turned `color-theme.ts` into `var()` strings; the waste-fraction form used
 * `gray800` as its default color), so a `var()` token is resolved against the
 * document first (`resolveColorString`, the one shared resolver — every other
 * colour-maths entry point in the app uses it too) and only then given up on.
 * Anything still unparseable — an empty string, `undefined`, a typo, a named
 * color the package doesn't know — degrades to the default gray preset instead
 * of taking the page down.
 */
export const resolveTagColor = (color?: TagColorType | string): string => {
  const preset = PRESET_COLORS[(color || 'gray') as TagColorType]
  const candidate = preset || color
  if (!candidate) return PRESET_COLORS.gray

  const resolved = resolveColorString(candidate)
  if (!resolved) return PRESET_COLORS.gray

  try {
    // `Color()` is the same parser the helpers below use — if it accepts the
    // string here, it cannot throw there.
    Color(resolved)
    return resolved
  } catch {
    return PRESET_COLORS.gray
  }
}

export const generateBoldHoverStyles = (color?: TagColorType) => {
  const hexColor = resolveTagColor(color)
  const darkenColor = Color(hexColor).darken(0.3).hex()
  return `
    &:hover {
      background-color: ${darkenColor};
      border-color: ${darkenColor};
      color: ${gray100};
    }
  `
}

export const generateBoldStyles = (color?: TagColorType) => {
  const hexColor = resolveTagColor(color)
  return `
    background-color: ${hexColor};
    border-color: ${hexColor};
    color: ${gray100};
  `
}

export const generateSubtleHoverStyles = (color?: TagColorType) => {
  const hexColor = resolveTagColor(color)
  const lightenColor = Color(hexColor).lighten(0.6).hex()
  const darkenColor = Color(hexColor).darken(0.6).hex()
  return `
    &:hover {
      background-color: ${lightenColor};
      border-color: ${lightenColor};
      color: ${darkenColor};
    }
  `
}

export const generateSubtleStyles = (color?: TagColorType) => {
  const hexColor = resolveTagColor(color)
  const lightenColor = Color(hexColor).lighten(0.7).hex()
  const darkenColor = Color(hexColor).darken(0.6).hex()
  return `
    background-color: ${lightenColor};
    border-color: ${lightenColor};
    color: ${darkenColor};
  `
}

export const generateMinimalHoverStyles = (color?: TagColorType) => {
  const hexColor = resolveTagColor(color)
  const lightenColor = Color(hexColor).lighten(0.9).hex()
  return `
    &:hover {
      background-color: ${lightenColor};
      border-color: ${hexColor};
      color: ${hexColor};
    }
  `
}

export const generateMinimalStyles = (color?: TagColorType) => {
  const hexColor = resolveTagColor(color)
  return `
    background-color: transparent;
    border-color: ${hexColor};
    color: ${hexColor};
  `
}
